import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { RefPersonalService } from '@pages/fabrica-credito/services/ref-personal.service';
import { IDeptoCiudad } from '@pages/fabrica-credito/interfaces/ref-personal';
import { ListPersonasService } from '../services/list-personas.service';
import { finalize } from 'rxjs/operators';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app-abc-lista-personas-dialog',
  templateUrl: './abc-lista-personas-dialog.component.html',
  providers: [MessageService]
})
export class AbcListaPersonasDialogComponent implements OnInit {
  form: FormGroup;
  title: string = 'Nueva Persona';
  loading: boolean = false;
  isEdit: boolean = false;

  // Arrays para los dropdowns
  departamentos: IDeptoCiudad[] = [];
  ciudades: IDeptoCiudad[] = [];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AbcListaPersonasDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private messageService: MessageService,
    private refPersonalService: RefPersonalService,
    private listPersonasService: ListPersonasService,
    private dialog: MatDialog
  ) {
    this.createForm();
  }

  ngOnInit(): void {
    this.cargarDepartamentos();

    // Suscribirse a cambios en el departamento
    this.form.get('departamento')?.valueChanges.subscribe(idDepartamento => {
      if (idDepartamento) {
        this.cargarCiudades(idDepartamento);
      } else {
        this.ciudades = [];
        this.form.patchValue({ ciudad: '' });
      }
    });

    if (this.data) {
      this.isEdit = true;
      this.title = 'Editar Persona';

      console.log(this.data);
      // Mapear los datos recibidos al formulario
      const formData = {
        numero_cliente: this.data.numero_cliente,
        primer_nombre: this.data.primer_nombre,
        segundo_nombre: this.data.segundo_nombre,
        apellido_paterno: this.data.apellido_paterno,
        apellido_materno: this.data.apellido_materno,
        fecha_creacion: this.convertirFecha(this.data.fechaAltaFormateada),
        no_radicado: this.data.numero_exterior,
        correo_electronico: this.data.ciudad, // El campo ciudad contiene el email
        departamento: this.data.estado,
        ciudad: this.data.del_muni,
        direccion: this.data.calle,
        barrio: this.data.colonia,
        codigo_postal: this.data.cp,
        celular: this.data.empresa,
        tipo_identificacion: this.data.telefono2,
        cedula: this.data.cedula,
        antiguedad: this.data.antiguedad,
        telefono_fijo: this.data.telefono,
        motivo_reporte: this.data.rfc,
        compania: this.data.compania
      };

      this.form.patchValue(formData);

      // Deshabilitar el campo número de cliente en modo edición
      this.form.get('numero_cliente').disable();

      // Si tenemos el departamento, cargamos las ciudades
      if (this.data.estado) {
        this.cargarCiudades(this.data.estado);
      }
    } else {
      // Establecer la fecha actual para nuevos registros
      const today = new Date().toISOString().split('T')[0];
      this.form.patchValue({ fecha_creacion: today });
    }

    // Deshabilitar el campo fecha_creacion
    this.form.get('fecha_creacion').disable();
  }

  onSubmit(): void {
    if (this.form.valid) {
      const formValue = this.form.getRawValue();
      const fechaCreacion = this.form.get('fecha_creacion').value;

      const request = {
        que: this.isEdit ? 2 : 1,
        tope: "",
        cmd: "",
        numero_cliente: this.isEdit ? this.data.numero_cliente : (formValue.numero_cliente || ""),
        primer_nombre: formValue.primer_nombre || "",
        segundo_nombre: formValue.segundo_nombre || "",
        apellido_paterno: formValue.apellido_paterno || "",
        apellido_materno: formValue.apellido_materno || "",
        sFechaNacimiento: "",
        calle: formValue.direccion || "",
        numero_exterior: formValue.no_radicado || "",
        colonia: formValue.barrio || "",
        ciudad: formValue.correo_electronico || "",
        del_muni: formValue.ciudad || "",
        estado: formValue.departamento || "",
        cp: Number(formValue.codigo_postal) || 0,
        empresa: formValue.celular || "",
        telefono: formValue.telefono_fijo || "",
        telefono2: formValue.tipo_identificacion || "",
        rfc: formValue.motivo_reporte || "",
        cedula: formValue.cedula || "",
        lnegodbc: this.isEdit ? (this.data?.lnegodbc || 0) : 0,
        compania: formValue.compania || ""
      };

      this.loading = true;

      if (this.isEdit) {
        this.listPersonasService.updatePersona(request)
          .pipe(finalize(() => this.loading = false))
          .subscribe({
            next: (response) => {
              if (response.success) {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Éxito',
                  detail: 'Persona actualizada exitosamente'
                });
                this.dialogRef.close(true);
              } else {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Error al actualizar la persona'
                });
              }
            },
            error: (error) => {
              console.error('Error al actualizar persona:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al actualizar la persona'
              });
            }
          });
      } else {
        this.listPersonasService.addListaPersona(request)
          .pipe(finalize(() => this.loading = false))
          .subscribe({
            next: (response) => {
              if (response.success) {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Éxito',
                  detail: 'Persona guardada exitosamente'
                });
                this.dialogRef.close(true);
              } else {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Error al guardar la persona'
                });
              }
            },
            error: (error) => {
              console.error('Error al guardar persona:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al guardar la persona'
              });
            }
          });
      }
    }
  }

  customizables(): void {
    const dialogRef = this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: "1000px",
      height: "1000px",
      disableClose: true,
      panelClass: ["custom-dialog-zindex"],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: "cteneg",
        sClave: this.data.numero_cliente,
        sNombre: `${this.data.primer_nombre} ${this.data.apellido_paterno}`,
        sTipo: "Opcion",
      },
    });
  }

  private cargarDepartamentos(): void {
    this.loading = true;
    this.refPersonalService.getDeptoCiudad({
      identificador: 1,
      valor: ''
    })
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.departamentos = response.data;
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se pudieron cargar los departamentos'
            });
          }
        },
        error: (error) => {
          console.error('Error al cargar departamentos:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los departamentos'
          });
        }
      });
  }

  private cargarCiudades(idDepartamento: string): void {
    this.loading = true;
    this.refPersonalService.getDeptoCiudad({
      identificador: 2,
      valor: idDepartamento
    })
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.ciudades = response.data;
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: 'Advertencia',
              detail: 'No se pudieron cargar las ciudades'
            });
          }
        },
        error: (error) => {
          console.error('Error al cargar ciudades:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar las ciudades'
          });
        }
      });
  }

  createForm(): void {
    this.form = this.fb.group({
      numero_cliente: [''],
      primer_nombre: [''],
      segundo_nombre: [''],
      apellido_paterno: [''],
      apellido_materno: [''],
      fecha_creacion: [''],
      no_radicado: [''],
      correo_electronico: ['', [Validators.email]],
      departamento: [''],
      ciudad: [''],
      direccion: [''],
      barrio: [''],
      codigo_postal: [''],
      celular: [''],
      tipo_identificacion: [''],
      cedula: [''],
      antiguedad: [''],
      telefono_fijo: [''],
      motivo_reporte: [''],
      compania: ['']
    });
  }

  close(): void {
    this.dialogRef.close(false);
  }

  private convertirFecha(fecha: string): string {
    if (!fecha) return '';
    const [dia, mes, anio] = fecha.split('/');
    return `${anio}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
  }
} 